import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Book,
  Globe,
  FileText,
  Video,
  Archive,
  Plus,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Library,
  Users,
  Star,
  Filter,
  Pencil,
  Trash2,
  Shield,
  Building2,
  X,
  Eye,
  Download
} from 'lucide-react';
import { authenticatedFetch, getUser } from '@/lib/auth';
import LicenseDisclaimerModal from './LicenseDisclaimerModal';
import OwnLicenseRequiredModal from './OwnLicenseRequiredModal';
import { LicenseFormData } from './InlineLicenseForm';

interface SharedLiterature {
  id: string;
  title: string;
  original_url: string | null;
  isbn: string | null;
  doi: string | null;
  author: string | null;
  publisher: string | null;
  publication_year: number | null;
  language: string;
  description: string | null;
  literature_type: string;
  download_status: string;
  local_file_path: string | null;
  usage_count: number;
  quality_rating: number | null;
  categories: string[];
  tags: string[];
  license_type: string | null;
  cc_variant: string | null;
  allows_distribution: boolean | null;
  allows_modification: boolean | null;
  allows_commercial_use: boolean | null;
  requires_attribution: boolean | null;
  attribution_text: string | null;
  rights_holder: string | null;
  access_level: 'open' | 'restricted' | 'unknown';
  created_at: string;
  created_by: string | null;
  created_by_org_id: string | null;
  created_by_org_name: string | null;
  link_count: number;
}

interface SharingRulesResponse {
  canShare: boolean;
  requiresDisclaimer: boolean;
  requiresOwnLicense: boolean;
  inheritLicense: boolean;
  reason?: string;
  warnings: string[];
  license?: {
    license_type: string;
    cc_variant?: string | null;
    requires_attribution?: boolean;
    attribution_text?: string;
    allows_modification?: boolean;
    allows_commercial_use?: boolean;
    allows_distribution?: boolean;
    license_url?: string;
    rights_holder?: string;
    valid_until?: string;
    usage_notes?: string;
    is_organization_specific?: boolean;
    max_users?: number;
  };
}

interface SharedLiteratureBrowserProps {
  onSelect?: (literature: SharedLiterature) => void;
  studyPlanId?: string;
  mode?: 'browse' | 'select';
}

export function SharedLiteratureBrowser({
  onSelect,
  studyPlanId,
  mode = 'browse'
}: SharedLiteratureBrowserProps) {
  const [literature, setLiterature] = useState<SharedLiterature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [literatureType, setLiteratureType] = useState<string>('');
  const [hasArchive, setHasArchive] = useState(false);
  const [accessLevel, setAccessLevel] = useState<string>('');  // 'open', 'restricted', or ''
  const [orderBy, setOrderBy] = useState('usage_count');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  // License modal state
  const [selectedLiterature, setSelectedLiterature] = useState<SharedLiterature | null>(null);
  const [sharingRules, setSharingRules] = useState<SharingRulesResponse | null>(null);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showOwnLicenseModal, setShowOwnLicenseModal] = useState(false);
  const [addingToStudyPlan, setAddingToStudyPlan] = useState(false);

  // Track already-added literature IDs
  const [alreadyAddedIds, setAlreadyAddedIds] = useState<Set<string>>(new Set());

  // Current user organization (for edit/delete permissions)
  const [currentUserOrgId, setCurrentUserOrgId] = useState<string | null>(null);

  // Edit modal state
  const [editingLit, setEditingLit] = useState<SharedLiterature | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    author: '',
    publisher: '',
    publication_year: '',
    original_url: '',
    description: '',
    isbn: '',
    doi: '',
    literature_type: 'webpage'
  });
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deletingLit, setDeletingLit] = useState<SharedLiterature | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Archived content viewer
  const [viewingArchive, setViewingArchive] = useState<SharedLiterature | null>(null);
  const [archiveInfo, setArchiveInfo] = useState<{
    file_exists: boolean;
    file_size: number;
    file_type: string;
    has_extracted_text: boolean;
    extracted_text_preview: string | null;
  } | null>(null);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [showExtractedText, setShowExtractedText] = useState(false);

  const fetchLiterature = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (literatureType) params.set('literature_type', literatureType);
      if (hasArchive) params.set('has_archive', 'true');
      if (accessLevel) params.set('access_level', accessLevel);
      params.set('order_by', orderBy);
      params.set('limit', String(pagination.limit));
      params.set('offset', String(reset ? 0 : pagination.offset));

      const response = await authenticatedFetch(`/api/shared-literature?${params}`);

      if (!response.ok) {
        throw new Error('Kunne ikke hente litteratur');
      }

      const data = await response.json();

      if (reset) {
        setLiterature(data.data);
      } else {
        setLiterature(prev => [...prev, ...data.data]);
      }
      setPagination(data.pagination);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, literatureType, hasArchive, orderBy, pagination.limit, pagination.offset]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/shared-literature/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchLiterature(true);
    fetchStats();
  }, [search, literatureType, hasArchive, orderBy]);

  // Get current user's member organization
  useEffect(() => {
    const user = getUser();
    if (user) {
      setCurrentUserOrgId(user.member_id || null);
    }
  }, []);

  // Fetch already-added literature IDs when in select mode
  useEffect(() => {
    if (mode === 'select' && studyPlanId) {
      const fetchAlreadyAdded = async () => {
        try {
          const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`);
          if (response.ok) {
            const data = await response.json();
            const ids = new Set<string>(
              data.literature
                .filter((lit: any) => lit.shared_literature_id)
                .map((lit: any) => lit.shared_literature_id)
            );
            setAlreadyAddedIds(ids);
          }
        } catch (err) {
          console.error('Failed to fetch already added literature:', err);
        }
      };
      fetchAlreadyAdded();
    }
  }, [mode, studyPlanId]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchLiterature(true);
  };

  const handleLoadMore = () => {
    setPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
    fetchLiterature(false);
  };

  // Check sharing rules before adding literature
  const checkSharingRules = async (lit: SharedLiterature): Promise<SharingRulesResponse | null> => {
    try {
      const response = await authenticatedFetch('/api/literature/check-sharing-rules', {
        method: 'POST',
        body: JSON.stringify({
          shared_literature_id: lit.id
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kunne ikke sjekke delingsregler');
      }

      const data = await response.json();
      // Backend returns sharing rules directly in response, not wrapped in data
      return data as SharingRulesResponse;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  // Main handler for add button click
  const handleAddToStudyPlan = async (lit: SharedLiterature) => {
    if (!studyPlanId) return;

    setSelectedLiterature(lit);
    setError(null);

    // Check sharing rules
    const rules = await checkSharingRules(lit);
    if (!rules) return;

    setSharingRules(rules);

    // Determine which modal to show based on rules
    if (rules.requiresOwnLicense) {
      // Closed license - user must provide their own license
      setShowOwnLicenseModal(true);
    } else if (rules.requiresDisclaimer) {
      // Open license - show disclaimer
      setShowDisclaimerModal(true);
    } else if (rules.canShare) {
      // Direct add (rare edge case - e.g., public domain)
      await doAddToStudyPlan(lit, {
        disclaimer_accepted: true,
        inherit_license: rules.inheritLicense
      });
    } else {
      // Cannot share at all
      setError(rules.reason || 'Denne litteraturen kan ikke deles.');
    }
  };

  // Actually add the literature to study plan
  const doAddToStudyPlan = async (
    lit: SharedLiterature,
    options: {
      disclaimer_accepted?: boolean;
      own_license?: LicenseFormData;
      inherit_license?: boolean;
    }
  ) => {
    if (!studyPlanId) return;

    setAddingToStudyPlan(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature/link`, {
        method: 'POST',
        body: JSON.stringify({
          shared_literature_id: lit.id,
          disclaimer_accepted: options.disclaimer_accepted || false,
          inherit_license: options.inherit_license || false,
          own_license: options.own_license || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kunne ikke legge til litteratur');
      }

      // Success - close modals, update state, and notify parent
      setShowDisclaimerModal(false);
      setShowOwnLicenseModal(false);
      setSelectedLiterature(null);
      setSharingRules(null);

      // Mark this item as already added
      setAlreadyAddedIds(prev => new Set([...prev, lit.id]));

      if (onSelect) {
        onSelect(lit);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAddingToStudyPlan(false);
    }
  };

  // Handler for disclaimer modal accept
  const handleDisclaimerAccept = () => {
    if (!selectedLiterature) return;
    doAddToStudyPlan(selectedLiterature, {
      disclaimer_accepted: true,
      inherit_license: sharingRules?.inheritLicense || false
    });
  };

  // Handler for own license modal confirm
  const handleOwnLicenseConfirm = (ownLicense: LicenseFormData) => {
    if (!selectedLiterature) return;
    doAddToStudyPlan(selectedLiterature, {
      own_license: ownLicense
    });
  };

  // Handler for modal cancel
  const handleModalCancel = () => {
    setShowDisclaimerModal(false);
    setShowOwnLicenseModal(false);
    setSelectedLiterature(null);
    setSharingRules(null);
  };

  // Check if user can edit/delete this item
  const canEditDelete = (lit: SharedLiterature): boolean => {
    if (!currentUserOrgId) return false;
    return lit.created_by_org_id === currentUserOrgId;
  };

  // Open edit modal
  const handleEdit = (lit: SharedLiterature) => {
    setEditingLit(lit);
    setEditForm({
      title: lit.title || '',
      author: lit.author || '',
      publisher: lit.publisher || '',
      publication_year: lit.publication_year?.toString() || '',
      original_url: lit.original_url || '',
      description: lit.description || '',
      isbn: lit.isbn || '',
      doi: lit.doi || '',
      literature_type: lit.literature_type || 'webpage'
    });
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editingLit) return;
    setSaving(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`/api/shared-literature/${editingLit.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: editForm.title,
          author: editForm.author || null,
          publisher: editForm.publisher || null,
          publication_year: editForm.publication_year ? parseInt(editForm.publication_year) : null,
          original_url: editForm.original_url || null,
          description: editForm.description || null,
          isbn: editForm.isbn || null,
          doi: editForm.doi || null,
          literature_type: editForm.literature_type
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kunne ikke oppdatere');
      }

      setEditingLit(null);
      fetchLiterature(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Confirm delete
  const handleDelete = async () => {
    if (!deletingLit) return;
    setDeleting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`/api/shared-literature/${deletingLit.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kunne ikke slette');
      }

      setDeletingLit(null);
      fetchLiterature(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // View archived content
  const handleViewArchive = async (lit: SharedLiterature) => {
    setViewingArchive(lit);
    setArchiveInfo(null);
    setExtractedText(null);
    setShowExtractedText(false);
    setLoadingArchive(true);

    try {
      const response = await authenticatedFetch(`/api/shared-literature/${lit.id}/archived-content?format=info`);
      if (response.ok) {
        const data = await response.json();
        setArchiveInfo(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch archive info:', err);
    } finally {
      setLoadingArchive(false);
    }
  };

  // Load extracted text
  const handleLoadExtractedText = async () => {
    if (!viewingArchive) return;
    setLoadingArchive(true);

    try {
      const response = await authenticatedFetch(`/api/shared-literature/${viewingArchive.id}/extracted-text`);
      if (response.ok) {
        const data = await response.json();
        setExtractedText(data.data.extracted_text);
        setShowExtractedText(true);
      }
    } catch (err) {
      console.error('Failed to fetch extracted text:', err);
    } finally {
      setLoadingArchive(false);
    }
  };

  // Open PDF in new tab
  const handleOpenPdf = () => {
    if (!viewingArchive) return;
    window.open(`/api/shared-literature/${viewingArchive.id}/archived-content`, '_blank');
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'word':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'webpage':
        return <Globe className="w-5 h-5 text-green-500" />;
      case 'video':
        return <Video className="w-5 h-5 text-purple-500" />;
      default:
        return <Book className="w-5 h-5 text-gray-500" />;
    }
  };

  const getDownloadStatusBadge = (status: string) => {
    switch (status) {
      case 'downloaded':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
            <Archive className="w-3 h-3" />
            Arkivert
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" />
            Venter
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
            <AlertCircle className="w-3 h-3" />
            Feilet
          </span>
        );
      default:
        return null;
    }
  };

  const getAccessLevelBadge = (accessLevel: string, licenseType: string | null, ccVariant: string | null) => {
    if (accessLevel === 'restricted') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">
          <AlertCircle className="w-3 h-3" />
          Begrenset tilgang
        </span>
      );
    }
    if (licenseType && licenseType !== 'unknown') {
      const label = licenseType === 'creative_commons'
        ? (ccVariant ? `CC ${ccVariant.replace('CC-', '')}` : 'CC-lisens')
        : licenseType === 'public_domain' ? 'Offentlig domene'
        : licenseType === 'open_access' ? 'Åpen tilgang'
        : licenseType === 'citation_right' ? 'Sitatrett'
        : licenseType === 'kopinor_agreement' ? 'Kopinor'
        : licenseType === 'proprietary' ? 'Proprietær'
        : licenseType === 'subscription' ? 'Abonnement'
        : licenseType === 'purchased' ? 'Kjøpt'
        : licenseType === 'educational' ? 'Utdanning'
        : licenseType;

      const isOpen = ['public_domain', 'creative_commons', 'open_access', 'citation_right'].includes(licenseType);

      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
          isOpen ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
        }`}>
          <Shield className="w-3 h-3" />
          {label}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
        <AlertCircle className="w-3 h-3" />
        Ikke spesifisert
      </span>
    );
  };

  // Get license permission badges
  const getLicensePermissionBadges = (lit: SharedLiterature) => {
    if (!lit.license_type || lit.license_type === 'unknown') return null;

    const badges = [];
    if (lit.allows_distribution === true) {
      badges.push(<span key="dist" className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Kan deles</span>);
    } else if (lit.allows_distribution === false) {
      badges.push(<span key="dist" className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded">Kan ikke deles</span>);
    }
    if (lit.allows_modification === false) {
      badges.push(<span key="mod" className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">Ingen endring</span>);
    }
    if (lit.allows_commercial_use === false) {
      badges.push(<span key="com" className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">Ikke-kommersiell</span>);
    }
    if (lit.requires_attribution) {
      badges.push(<span key="attr" className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Kreditering</span>);
    }

    return badges.length > 0 ? <div className="flex flex-wrap gap-1 mt-1">{badges}</div> : null;
  };

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      {mode === 'browse' && stats && (
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-100">
          <div className="flex items-center gap-3 mb-3">
            <Library className="w-6 h-6 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">Delt litteraturbibliotek</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Totalt</div>
              <div className="font-semibold text-gray-900">{stats.total || 0} kilder</div>
            </div>
            <div>
              <div className="text-gray-500">Arkivert</div>
              <div className="font-semibold text-green-600">{stats.archived || 0} filer</div>
            </div>
            <div>
              <div className="text-gray-500">Bruk</div>
              <div className="font-semibold text-gray-900">{stats.total_usage || 0} ganger</div>
            </div>
          </div>
        </div>
      )}

      {/* Search and filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Søk i tittel, forfatter, utgiver..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 border rounded-lg flex items-center gap-1 ${
              showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-300 text-gray-700'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filter
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Søk
          </button>
        </form>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={literatureType}
                onChange={(e) => setLiteratureType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Alle typer</option>
                <option value="pdf">PDF</option>
                <option value="word">Word</option>
                <option value="webpage">Nettside</option>
                <option value="video">Video</option>
                <option value="manual">Manuell</option>
                <option value="other">Annet</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tilgang</label>
              <select
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Alle</option>
                <option value="open">Åpen (kan deles)</option>
                <option value="restricted">Begrenset (lisensbeskyttet)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sortering</label>
              <select
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="usage_count">Mest brukt</option>
                <option value="created_at">Nyeste først</option>
                <option value="title">Alfabetisk</option>
                <option value="quality_rating">Høyest kvalitet</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasArchive}
                  onChange={(e) => setHasArchive(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Kun arkiverte</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Literature list */}
      <div className="space-y-2">
        {literature.map((lit) => (
          <div
            key={lit.id}
            className="bg-white rounded-lg border border-gray-200 hover:border-indigo-300 transition-colors"
          >
            <div className="p-4">
              <div className="flex items-start gap-3">
                {getTypeIcon(lit.literature_type)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-medium text-gray-900 truncate">{lit.title}</h3>
                      {lit.author && (
                        <p className="text-sm text-gray-500">{lit.author}</p>
                      )}
                      {/* Organization info */}
                      {lit.created_by_org_name && (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {lit.created_by_org_name}
                          {canEditDelete(lit) && (
                            <span className="text-indigo-500 font-medium">(din organisasjon)</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {getDownloadStatusBadge(lit.download_status)}
                      {getAccessLevelBadge(lit.access_level, lit.license_type, lit.cc_variant)}
                    </div>
                  </div>

                  {/* License permission badges */}
                  {getLicensePermissionBadges(lit)}

                  <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                    {lit.usage_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {lit.usage_count} studieplan{lit.usage_count !== 1 && 'er'}
                      </span>
                    )}
                    {lit.quality_rating && (
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500" />
                        {lit.quality_rating}/5
                      </span>
                    )}
                    {lit.publication_year && (
                      <span>{lit.publication_year}</span>
                    )}
                    {lit.original_url && (
                      <a
                        href={lit.original_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                        Åpne
                      </a>
                    )}
                    {lit.download_status === 'downloaded' && lit.local_file_path && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewArchive(lit);
                        }}
                        className="flex items-center gap-1 text-green-600 hover:text-green-800"
                      >
                        <Eye className="w-4 h-4" />
                        Se arkiv
                      </button>
                    )}
                  </div>

                  {/* Expandable details */}
                  {expandedId === lit.id && (
                    <div className="mt-3 pt-3 border-t border-gray-100 text-sm">
                      {lit.description && (
                        <p className="text-gray-600 mb-2">{lit.description}</p>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-gray-500">
                        {lit.publisher && (
                          <div>
                            <span className="font-medium">Utgiver:</span> {lit.publisher}
                          </div>
                        )}
                        {lit.isbn && (
                          <div>
                            <span className="font-medium">ISBN:</span> {lit.isbn}
                          </div>
                        )}
                        {lit.doi && (
                          <div>
                            <span className="font-medium">DOI:</span> {lit.doi}
                          </div>
                        )}
                        {lit.language && (
                          <div>
                            <span className="font-medium">Språk:</span> {lit.language}
                          </div>
                        )}
                      </div>
                      {lit.categories && lit.categories.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {lit.categories.map((cat, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2">
                  {/* Edit/Delete buttons for user's own organization */}
                  {mode === 'browse' && canEditDelete(lit) && (
                    <>
                      {lit.link_count === 0 ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleEdit(lit)}
                            className="p-2 text-gray-400 hover:text-indigo-600 rounded"
                            title="Rediger"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeletingLit(lit)}
                            className="p-2 text-gray-400 hover:text-red-600 rounded"
                            title="Slett"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 px-2">
                          I bruk ({lit.link_count})
                        </span>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setExpandedId(expandedId === lit.id ? null : lit.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded"
                    title="Vis detaljer"
                  >
                    {expandedId === lit.id ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                  {mode === 'select' && studyPlanId && (
                    alreadyAddedIds.has(lit.id) ? (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-sm">
                        <CheckCircle className="w-4 h-4" />
                        <span>Lagt til</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAddToStudyPlan(lit)}
                        disabled={addingToStudyPlan}
                        className="p-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400"
                        title="Legg til i studieplan"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {literature.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            <Library className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Ingen litteratur funnet</p>
            <p className="text-sm">Prøv å justere søkekriteriene</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
          </div>
        )}

        {pagination.hasMore && !loading && (
          <button
            onClick={handleLoadMore}
            className="w-full py-3 text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Last inn flere ({pagination.total - literature.length} gjenstår)
          </button>
        )}
      </div>

      {/* License Disclaimer Modal - for open licenses */}
      {showDisclaimerModal && selectedLiterature && sharingRules?.license && (
        <LicenseDisclaimerModal
          literature={{
            id: selectedLiterature.id,
            title: selectedLiterature.title,
            author: selectedLiterature.author || undefined,
            original_url: selectedLiterature.original_url || undefined,
            publisher: selectedLiterature.publisher || undefined
          }}
          license={{
            license_type: sharingRules.license.license_type as any,
            cc_variant: sharingRules.license.cc_variant as any,
            requires_attribution: sharingRules.license.requires_attribution,
            attribution_text: sharingRules.license.attribution_text,
            allows_modification: sharingRules.license.allows_modification,
            allows_commercial_use: sharingRules.license.allows_commercial_use,
            allows_distribution: sharingRules.license.allows_distribution,
            license_url: sharingRules.license.license_url,
            rights_holder: sharingRules.license.rights_holder,
            valid_until: sharingRules.license.valid_until,
            usage_notes: sharingRules.license.usage_notes
          }}
          onAccept={handleDisclaimerAccept}
          onCancel={handleModalCancel}
          isLoading={addingToStudyPlan}
        />
      )}

      {/* Own License Required Modal - for closed licenses */}
      {showOwnLicenseModal && selectedLiterature && (
        <OwnLicenseRequiredModal
          literature={{
            id: selectedLiterature.id,
            title: selectedLiterature.title,
            author: selectedLiterature.author || undefined,
            original_url: selectedLiterature.original_url || undefined,
            publisher: selectedLiterature.publisher || undefined
          }}
          originalLicense={sharingRules?.license ? {
            license_type: sharingRules.license.license_type as any,
            cc_variant: sharingRules.license.cc_variant as any,
            valid_until: sharingRules.license.valid_until,
            is_organization_specific: sharingRules.license.is_organization_specific,
            max_users: sharingRules.license.max_users
          } : null}
          reason={sharingRules?.reason}
          onConfirm={handleOwnLicenseConfirm}
          onCancel={handleModalCancel}
          isLoading={addingToStudyPlan}
        />
      )}

      {/* Edit Modal */}
      {editingLit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Rediger litteratur</h3>
              <button
                onClick={() => setEditingLit(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tittel *</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Forfatter</label>
                <input
                  type="text"
                  value={editForm.author}
                  onChange={(e) => setEditForm({ ...editForm, author: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utgiver</label>
                  <input
                    type="text"
                    value={editForm.publisher}
                    onChange={(e) => setEditForm({ ...editForm, publisher: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utgivelsesår</label>
                  <input
                    type="number"
                    value={editForm.publication_year}
                    onChange={(e) => setEditForm({ ...editForm, publication_year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="1900"
                    max="2100"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input
                  type="url"
                  value={editForm.original_url}
                  onChange={(e) => setEditForm({ ...editForm, original_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ISBN</label>
                  <input
                    type="text"
                    value={editForm.isbn}
                    onChange={(e) => setEditForm({ ...editForm, isbn: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">DOI</label>
                  <input
                    type="text"
                    value={editForm.doi}
                    onChange={(e) => setEditForm({ ...editForm, doi: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={editForm.literature_type}
                  onChange={(e) => setEditForm({ ...editForm, literature_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="webpage">Nettside</option>
                  <option value="pdf">PDF</option>
                  <option value="word">Word</option>
                  <option value="video">Video</option>
                  <option value="manual">Manuell</option>
                  <option value="other">Annet</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setEditingLit(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Avbryt
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editForm.title.trim()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
              >
                {saving ? 'Lagrer...' : 'Lagre'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingLit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Slett litteratur</h3>
            </div>
            <div className="p-4">
              <p className="text-gray-700 mb-2">
                Er du sikker på at du vil slette denne litteraturen fra det delte biblioteket?
              </p>
              <p className="font-medium text-gray-900 mb-4">"{deletingLit.title}"</p>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>Merk:</strong> Denne handlingen kan ikke angres.
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setDeletingLit(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Avbryt
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
              >
                {deleting ? 'Sletter...' : 'Slett'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archived Content Viewer Modal */}
      {viewingArchive && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Archive className="w-5 h-5 text-green-600" />
                  Arkivert innhold
                </h3>
                <p className="text-sm text-gray-500 mt-1">{viewingArchive.title}</p>
              </div>
              <button
                onClick={() => {
                  setViewingArchive(null);
                  setArchiveInfo(null);
                  setExtractedText(null);
                  setShowExtractedText(false);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-y-auto">
              {loadingArchive ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : archiveInfo ? (
                <div className="space-y-4">
                  {/* File info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Filinformasjon</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Filtype:</span>
                        <span className="ml-2 font-medium">{archiveInfo.file_type.toUpperCase()}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Størrelse:</span>
                        <span className="ml-2 font-medium">{formatFileSize(archiveInfo.file_size)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Fil tilgjengelig:</span>
                        <span className={`ml-2 font-medium ${archiveInfo.file_exists ? 'text-green-600' : 'text-red-600'}`}>
                          {archiveInfo.file_exists ? 'Ja' : 'Nei'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Ekstrahert tekst:</span>
                        <span className={`ml-2 font-medium ${archiveInfo.has_extracted_text ? 'text-green-600' : 'text-gray-400'}`}>
                          {archiveInfo.has_extracted_text ? 'Tilgjengelig' : 'Ikke tilgjengelig'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    {archiveInfo.file_exists && archiveInfo.file_type === 'pdf' && (
                      <button
                        onClick={handleOpenPdf}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      >
                        <Eye className="w-4 h-4" />
                        Åpne PDF
                      </button>
                    )}
                    {archiveInfo.file_exists && (
                      <a
                        href={`/api/shared-literature/${viewingArchive.id}/archived-content`}
                        download
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        <Download className="w-4 h-4" />
                        Last ned
                      </a>
                    )}
                    {archiveInfo.has_extracted_text && !showExtractedText && (
                      <button
                        onClick={handleLoadExtractedText}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        <FileText className="w-4 h-4" />
                        Vis ekstrahert tekst
                      </button>
                    )}
                  </div>

                  {/* Preview */}
                  {archiveInfo.extracted_text_preview && !showExtractedText && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Tekstforhåndsvisning</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {archiveInfo.extracted_text_preview}
                      </p>
                    </div>
                  )}

                  {/* Full extracted text */}
                  {showExtractedText && extractedText && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Ekstrahert tekst</h4>
                        <button
                          onClick={() => setShowExtractedText(false)}
                          className="text-sm text-indigo-600 hover:text-indigo-800"
                        >
                          Skjul
                        </button>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                          {extractedText}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Warning if file doesn't exist */}
                  {!archiveInfo.file_exists && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800">
                        <strong>Advarsel:</strong> Den arkiverte filen ble ikke funnet på serveren.
                        Filen kan ha blitt slettet eller flyttet.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Kunne ikke laste arkivinformasjon</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setViewingArchive(null);
                  setArchiveInfo(null);
                  setExtractedText(null);
                  setShowExtractedText(false);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
